import { Component, Injector, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { AbstractComponent, CrmService, Pager, Team, TeamService, User, UserService } from 'lib-trend-core';
import { debounceTime, Subject, takeUntil } from 'rxjs';

interface chartData {
  name: string;
  value: number;
  series?: Array<{ name: string; value: number }>;
}

@Component({
    selector: 'app-crm',
    templateUrl: './crm.component.html',
    styleUrl: './crm.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CrmComponent extends AbstractComponent {
  pager: Pager<any> = new Pager<any>({ perPage: 10 });
  orderPager: Pager<any> = new Pager<any>({ perPage: 10 });
  // pager: Pager<CustomerCRM> = new Pager<CustomerCRM>({ perPage: 10 });
  @ViewChild('picker', { static: false }) picker: MatDatepicker<Date>;
  isPicker: boolean = false;

  private termOfSearch: Subject<string> = new Subject<string>();

  public listUser: Array<User> = new Array<User>();
  public listTeam: Array<Team> = new Array<Team>();

  public kpis: Record<string, number>;
  public revenueBySeller: chartData[] = [];
  public ordersChart: chartData[] = [];

  public labelUser: string = 'Atendente';

  colorScheme: any = {
    domain: ['#0acf97', '#4213f6'],
  }
  single: any[] = [
    {
      "name": "Ana",
      "value": 8940000
    },
    {
      "name": "Burno",
      "value": 5000000
    },
    {
      "name": "Carla",
      "value": 7200000
    },
    {
      "name": "Carla",
      "value": 7200000
    },
    {
      "name": "Carla",
      "value": 7200000
    },
  ]
  multi: any[];

  // options
  showXAxis = true;
  showYAxis = true;
  gradient = false;
  showXAxisLabel = true;
  showYAxisLabel = true;


  constructor(
    injector: Injector,
    public userService: UserService,
    public teamService: TeamService,
    public crmService: CrmService
  ) {
    super(injector);
    this.createForm();
  }

  ngOnInit(): void {
    this.getCrmMetrics();
    this.getRevenueBySeller();
    this.getOrdersChart();
    this.getCustomers();
    this.getOrders();
    if (this.isAdmin()) {
      this.getListUser();
    } else if (this.isSupervisor() || this.isAttendant()) {
      this.getListTeam();
    }
  }

  getCrmMetrics(): void {

    const formValues = this.formGroup.getRawValue();

    const filters: Record<string, unknown> = {
      ...(formValues.dateRange?.dateStart && formValues.dateRange?.dateEnd
        ? {
          dateStart: formValues.dateRange.dateStart.getTime(),
          dateEnd: formValues.dateRange.dateEnd.getTime(),
        }
        : {}),
    };

    this.searchParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, unknown>);

    this.crmService.getCrmMetrics(this.searchParams).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result: Record<string, number>) => {
        this.kpis = result;
      }
    });
  }

  getRevenueBySeller(): void {
    const formValues = this.formGroup.getRawValue();

    const filters: Record<string, unknown> = {
      ...(formValues.dateRange?.dateStart && formValues.dateRange?.dateEnd
        ? {
          dateStart: formValues.dateRange.dateStart.getTime(),
          dateEnd: formValues.dateRange.dateEnd.getTime(),
        }
        : {}),
    };

    this.searchParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, unknown>);

    this.crmService.getRevenueBySeller(this.searchParams).subscribe({
      next: (result: chartData[]) => {
        this.mountRevenueBySellerData(result);
      }
    });
  }

  getOrdersChart(): void {
    const queryParams = this.formGroup.value;
    this.crmService.getOrderChart(queryParams.dateRange).subscribe({
      next: (result: chartData[]) => {
        this.moutOrdersChartData(result);
      }
    });
  }

  getCustomers(): void {
    this.crmService.getCustomers(this.pager.page, this.pager.perPage, this.searchParams, null).subscribe({
      next: (pager: Pager<any>) => {
        this.pager = pager;
      }
    });
  }

  getOrders(): void {
    this.crmService.getOrders(this.orderPager.page, this.orderPager.perPage, this.searchParams, null).subscribe({
      next: (pager: Pager<any>) => {
        this.orderPager = pager;
      }
    });
  }

  loadPage(page: number) {
    this.pager.page = page;
    this.getCustomers();
  }

  loadOrderPage(page: number) {
    this.orderPager.page = page;
    this.getOrders();
  }

  onPeriodChange(event: any) {
    const today = new Date();
    const period = parseInt(event.value, 10);

    if (!isNaN(period)) {
      this.formGroup.get('dateRange')?.patchValue({
        dateStart: new Date(today.setDate(today.getDate() - period)),
        dateEnd: new Date(),
      });
      this.picker?.close();
    }
  }

  private createForm(): void {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    this.formGroup = this.formBuilder.group({
      dateRange: this.formBuilder.group({
        dateStart: [thirtyDaysAgo],
        dateEnd: [today],
      }),
    });

    let previousValue = this.formGroup.value;

    this.formGroup.valueChanges
      .pipe(debounceTime(100))
      .subscribe((currentValue) => {
        if (
          previousValue.dateRange.dateStart !== currentValue.dateRange.dateStart ||
          previousValue.dateRange.dateEnd !== currentValue.dateRange.dateEnd
        ) {
          this.formGroup.patchValue({ status: null }, { emitEvent: false });
          this.pager.page = 1;
        }
        this.getCrmMetrics();
        previousValue = currentValue;
      });
  }

  private getListUser(): void {
    this.userService.getList().subscribe({
      next: (list: Array<User>) => {
        this.listUser = list;
      },
    })
  }

  private getListTeam(): void {
    this.teamService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (list: Array<Team>) => {
          this.listTeam = list
            .filter((team: Team) => {
              const userId = this.getCurrentUserUser()._id;
              const isAgent = team.agents.some(
                (agent: User) => agent._id === userId
              )
              const isSupervisor = team.supervisors.some(
                (supervisor: User) => supervisor._id === userId
              )
              return isAgent || isSupervisor;
            })
            .filter(
              (team: Team) => team.department && team.department.removed !== true
            )
        },
      })
  }

  private mountRevenueBySellerData(rawData: any[]): void {
    this.revenueBySeller = rawData.map(item => ({
      name: item.sellerName,
      value: parseFloat(item.totalRevenue?.$numberDecimal ?? '0'),

      series: []
    }));
  }

  private moutOrdersChartData(rawData: any[]): void {
    this.ordersChart = rawData.map(item => ({
      name: `Pedido - ${item.code} de ${item.date}`,
      value: parseFloat(item.total?.$numberDecimal ?? '0'),
    }));
  }

}
